<template>
  <div class="musubi-page musubi-page--deliverability">
    <header class="musubi-page__head">
      <h1>Deliverability</h1>
      <p class="text-muted">
        Sending health, sender authentication (SPF / DKIM / DMARC), and per-sender
        warm-up. Keeping bounce + complaint rates low and authentication green
        protects your reputation far more than any "warming" trick — a clean,
        validated list is the biggest lever.
      </p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>
    <p v-if="message" class="alert alert-success py-2 small">{{ message }}</p>

    <div v-if="loading" class="text-muted small"><CygnusIcon name="spinner" :spin="true" class="me-1" />Loading…</div>

    <template v-else-if="health">
      <!-- Sending health -->
      <div class="dl-kpis">
        <MusubiKpiCard label="Sent" :value="String(health.sent)" :delta="`last ${health.window_days}d`" icon="envelope" />
        <MusubiKpiCard label="Delivered" :value="pct(health.delivered_rate)" :delta="`${health.delivered} delivered`" icon="check-circle" />
        <MusubiKpiCard label="Bounce rate" :value="pct(health.bounce_rate)" :delta="`${health.bounced} bounced`" icon="refresh" :class="rateClass('bounce')" />
        <MusubiKpiCard label="Complaint rate" :value="pct(health.complaint_rate)" :delta="`${health.complained} complaints`" icon="flag" :class="rateClass('complaint')" />
      </div>
      <div class="dl-verdict" :class="`dl-verdict--${health.verdict}`">
        <CygnusIcon :name="verdictIcon" />
        <span>{{ verdictText }}</span>
      </div>

      <!-- Send safety slice 8: the daily allowance, read only -->
      <SendAllowanceWidget />

      <!-- Per-sender auth + ramp -->
      <MusubiSectionCard v-for="s in identities" :key="s.id" :title="s.from_email" icon="envelope" class="dl-sender">
        <div class="dl-sender__head">
          <span v-if="s.is_default" class="badge bg-primary">default</span>
          <span v-if="!s.is_active" class="badge bg-secondary">inactive</span>
          <span class="text-muted small">{{ s.label }}</span>
          <router-link :to="{ path: '/admin/senders', query: { identity: s.id } }" class="btn btn-link btn-sm p-0 ms-auto dl-sender__edit">
            <CygnusIcon name="pen" size="0.85em" /> Edit identity
          </router-link>
        </div>

        <div class="dl-auth">
          <span class="dl-chip" :class="s.auth.spf ? 'ok' : 'bad'"><CygnusIcon :name="s.auth.spf ? 'check' : 'x'" /> SPF</span>
          <span class="dl-chip" :class="s.auth.dkim ? 'ok' : 'bad'"><CygnusIcon :name="s.auth.dkim ? 'check' : 'x'" /> DKIM<span v-if="s.auth.dkim_selector" class="dl-sel">{{ s.auth.dkim_selector }}</span></span>
          <span class="dl-chip" :class="s.auth.dmarc ? 'ok' : 'bad'"><CygnusIcon :name="s.auth.dmarc ? 'check' : 'x'" /> DMARC<span v-if="s.auth.dmarc_policy" class="dl-sel">p={{ s.auth.dmarc_policy }}</span></span>
        </div>
        <p v-if="!s.auth.spf || !s.auth.dkim || !s.auth.dmarc" class="text-muted small mb-2">
          Missing records weaken inbox placement. Add the SPF / DKIM / DMARC DNS records for <strong>{{ s.domain }}</strong> (Mailgun provides the exact values).
        </p>

        <!-- Warm-up ramp -->
        <div class="dl-ramp">
          <label class="dl-toggle">
            <input type="checkbox" :checked="s.ramp.enabled" @change="toggleWarmup(s, $event.target.checked)">
            Warm-up ramp
          </label>
          <template v-if="s.ramp.enabled">
            <div class="dl-bar" :title="`${s.ramp.sent_today} sent today of ${s.ramp.cap} cap`">
              <div class="dl-bar__fill" :style="{ width: barPct(s.ramp) }"></div>
            </div>
            <span class="small text-muted">
              {{ s.ramp.sent_today }} / {{ s.ramp.cap }} today · {{ s.ramp.remaining }} left · day {{ s.ramp.age_days }}
            </span>
            <span class="dl-capedit">
              ceiling
              <input type="number" min="1" class="form-control form-control-sm" v-model.number="s._capMax" style="width:90px">
              <button class="btn btn-sm btn-link p-0" :disabled="s._saving" @click="saveCap(s)">save</button>
            </span>
          </template>
          <span v-else class="small text-muted">Off — sends are uncapped. Enable when warming a new from-address.</span>
        </div>
      </MusubiSectionCard>

      <p v-if="!identities.length" class="text-muted">No sender identities. Add one under Settings ▸ Senders.</p>
    </template>

    <!-- Enterprise BYO Mailgun — self-gating card (renders nothing unless the
         tenant is entitled). Bring-your-own sending infrastructure. -->
    <AdminByoMailgun />
  </div>
</template>

<script>
/**
 * AdminDeliverability — sending health + per-sender auth (SPF/DKIM/DMARC) +
 * warm-up ramp control. Reads /api/v1/admin/email/deliverability; toggles ramp
 * via /deliverability/warmup.
 */
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiKpiCard from "@root/components/ui/MusubiKpiCard.vue";
import MusubiSectionCard from "@root/components/ui/MusubiSectionCard.vue";
import AdminByoMailgun from "@root/components/AdminByoMailgun.vue";
import SendAllowanceWidget from "@root/components/SendAllowanceWidget.vue";

export default {
  name: 'AdminDeliverability',
  components: { MusubiKpiCard, MusubiSectionCard, AdminByoMailgun, SendAllowanceWidget },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return { api: useApi(), health: null, identities: [], loading: true, error: null, message: '' };
  },
  computed: {
    verdictText() {
      return {
        good: 'Healthy — bounce + complaint rates are within safe limits.',
        warn: 'Watch — bounce or complaint rate is elevated; clean the list / slow down.',
        bad: 'At risk — high bounce/complaint rate is damaging reputation. Pause + validate the list.',
        insufficient_data: 'Not enough recent sends to judge reputation yet.',
      }[this.health?.verdict] || '';
    },
    verdictIcon() {
      return {
        good: 'shield', warn: 'warning',
        bad: 'warning', insufficient_data: 'info-circle',
      }[this.health?.verdict] || 'info-circle';
    },
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true; this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/email/deliverability');
        const p = res?.data ?? res;
        this.health = p.health;
        this.identities = (p.identities || []).map(s => ({ ...s, _capMax: s.ramp?.cap ?? 500, _saving: false }));
      } catch (e) { this.error = e?.message || 'Failed to load deliverability.'; }
      this.loading = false;
    },
    pct(rate) { return (Math.round((rate || 0) * 1000) / 10) + '%'; },
    barPct(ramp) { return Math.min(100, Math.round((ramp.sent_today / Math.max(1, ramp.cap)) * 100)) + '%'; },
    rateClass(kind) {
      const v = this.health?.verdict;
      if (v === 'bad') return 'kpi--bad';
      if (v === 'warn') return 'kpi--warn';
      return '';
    },
    async toggleWarmup(s, enabled) {
      await this.saveWarmup(s, { enabled });
    },
    async saveCap(s) {
      await this.saveWarmup(s, { enabled: true, daily_cap_max: s._capMax });
    },
    async saveWarmup(s, payload) {
      s._saving = true; this.error = null; this.message = '';
      try {
        await this.api.post('/api/v1/admin/email/deliverability/warmup', { identity_id: s.id, ...payload });
        this.message = `Warm-up updated for ${s.from_email}.`;
        await this.load();
      } catch (e) { this.error = e?.message || 'Failed to update warm-up.'; }
      finally { s._saving = false; }
    },
  },
};
</script>

<style scoped>
.dl-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 12px; margin-bottom: 12px; }
.dl-verdict { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-radius: 8px; margin-bottom: 18px; font-size: 0.9rem; }
.dl-verdict--good { background: #e8f5e9; color: var(--m-status-success, #1b5e20); }
.dl-verdict--warn { background: #fff8e1; color: var(--m-status-warning, #8d6e00); }
.dl-verdict--bad { background: #fdecea; color: var(--m-status-danger, #b71c1c); }
.dl-verdict--insufficient_data { background: var(--m-bg-content, #f4f5f7); color: var(--m-text-tertiary, #666); }
.dl-sender { margin-bottom: 12px; }
.dl-sender__head { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.dl-auth { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 6px; }
.dl-chip { display: inline-flex; align-items: center; gap: 5px; padding: 2px 10px; border-radius: 999px; font-size: 0.8rem; border: 1px solid transparent; }
.dl-chip.ok { background: #e8f5e9; color: var(--m-status-success, #1b5e20); }
.dl-chip.bad { background: #fdecea; color: var(--m-status-danger, #b71c1c); }
.dl-sel { opacity: 0.7; font-size: 0.72rem; margin-left: 4px; }
.dl-ramp { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; margin-top: 8px; }
.dl-toggle { display: inline-flex; align-items: center; gap: 6px; font-weight: 600; font-size: 0.85rem; }
.dl-bar { flex: 1; min-width: 120px; max-width: 280px; height: 8px; background: var(--m-border, #e0e0e0); border-radius: 999px; overflow: hidden; }
.dl-bar__fill { height: 100%; background: var(--m-accent, #1976d2); }
.dl-capedit { display: inline-flex; align-items: center; gap: 6px; font-size: 0.8rem; color: var(--m-text-tertiary, #666); }
.kpi--warn :deep(.m-kpi__value) { color: var(--m-status-warning, #8d6e00); }
.kpi--bad :deep(.m-kpi__value) { color: var(--m-status-danger, #b71c1c); }
</style>
